import Vue from 'vue'

// API resources
import apiUser from '@/api/modules/user'
import apiClubSubscriptions from '@/api/modules/club-subscriptions'

// Mutations
import {
  ADD_CLUB_SUBSCRIPTIONS,
  UPDATE_CLUB_SUBSCRIPTIONS,
  UPDATE_CLUB_SUBSCRIPTION,
  ADD_CLUB_PORTFOLIOS_MISSING_INFO,
  ADD_CLUB_PORTFOLIOS_COMPLETE_INFO,
  UPDATE_CLUB_SUBSCRIPTION_STATE,
  ADD_CLUB_PRICING,
  RESET_MODULE,
  UPDATE_SIGN_URL
} from '@/store/mutation-types'

// Default state
const getDefaultState = () => ({
  subscriptions: null,
  signUrl: null,
  docusignWindow: null,
  pricing: {},
  portfoliosCompleteInfo: [],
  portfoliosMissingInfo: []
})
const state = getDefaultState()

// -----------------------------------------------------------------------------

const actions = {
  fetchClubSubscriptionById(context, id) {
    return apiUser.fetchClubSubscriptionById(id).then(data => {
      context.commit(UPDATE_CLUB_SUBSCRIPTION, data.clubSubscription)
    })
  },

  fetchClubSubscription(context, id) {
    return apiUser.fetchClubSubscription(id).then(data => {
      context.commit(ADD_CLUB_SUBSCRIPTIONS, data.clubSubscriptions)
    })
  },

  fetchAllClubSubscription(context, id) {
    return apiUser.fetchAllClubSubscription(id).then(data => {
      context.commit(ADD_CLUB_SUBSCRIPTIONS, data.clubSubscriptions)
    })
  },

  subscribeToClub({ commit }, payload) {
    return apiUser.subscribeToClub(payload).then(data => {
      commit(UPDATE_CLUB_SUBSCRIPTIONS, data.clubSubscription)
    })
  },

  updateSubscriptionStatus({ commit }, payload) {
    return apiUser.updateSubscriptionStatus(payload).then(data => {
      commit(UPDATE_CLUB_SUBSCRIPTIONS, data.clubSubscription)
    })
  },

  approveClubSubscription({ commit, dispatch }, payload) {
    return apiUser
      .approveClubSubscription(payload.subscriptionId, {
        clubSubscription: {
          portfolios: payload.portfolios
        }
      })
      .then(data => {
        commit(UPDATE_CLUB_SUBSCRIPTION, data.clubSubscription)
      })
  },

  revokeClubSubscription({ commit, dispatch }, payload) {
    return apiUser.deleteClubSubscription({ id: payload.subscriptionId }).then(data => {
      commit(UPDATE_CLUB_SUBSCRIPTION, data.clubSubscription)
    })
  },

  cancelClubSubscription({ commit, dispatch }, payload) {
    return apiUser
      .deleteClubSubscription({ id: payload.subscriptionId })
      .then(data => {
        commit(UPDATE_CLUB_SUBSCRIPTIONS, data.clubSubscription)
      })
  },

  rejectClubSubscription({ commit, dispatch }, payload) {
    return apiUser.rejectClubSubscription({ id: payload.subscriptionId }).then(data => {
      commit(UPDATE_CLUB_SUBSCRIPTION, data.clubSubscription)
    })
  },

  addPortfoliosMissingInfo(context, data) {
    context.commit(ADD_CLUB_PORTFOLIOS_MISSING_INFO, data)
  },

  addPortfoliosCompleteInfo(context, data) {
    context.commit(ADD_CLUB_PORTFOLIOS_COMPLETE_INFO, data)
  },

  fetchSignUrl({ commit }, subscriptionId) {
    return apiUser.fetchSignUrl(subscriptionId).then(data => {
      commit(UPDATE_SIGN_URL, data.signUrl)
    })
  },

  fetchLetterDownloadLink(context, subscriptionId) {
    return apiUser.fetchLetterDownloadLink(subscriptionId).then(data => {
      return data.downloadUrl
    })
  },

  updateClubSingleSubscription(context, data) {
    context.commit(UPDATE_CLUB_SUBSCRIPTION_STATE, data)
  },

  fetchClubPricing(context, payload) {
    return apiClubSubscriptions
      .fetchClubPricing(payload)
      .then(data => {
        return context.commit(ADD_CLUB_PRICING, data)
      })
      .catch(err => err)
  },

  resetState(context) {
    context.commit(RESET_MODULE)
  },

  postClubPaymentInfo(context, { subscriptionId, payload }) {
    return apiUser.postClubPaymentInfo(subscriptionId, payload).then(data => {
      context.commit(UPDATE_CLUB_SUBSCRIPTIONS, data.clubSubscription)
    })
  }
}

const mutations = {
  [ADD_CLUB_SUBSCRIPTIONS](state, data) {
    state.subscriptions = data
  },

  [UPDATE_CLUB_SUBSCRIPTIONS](state, data) {
    const index = state.subscriptions.indexOf(el => {
      return data.id === el.id
    })

    let subscriptions = state.subscriptions

    if (index < 0) {
      subscriptions.splice(0, 0, data)
    } else {
      subscriptions.splice(index, 1, data)
    }

    Vue.set(state, 'subscriptions', subscriptions)
  },

  [UPDATE_CLUB_SUBSCRIPTION](state, data) {
    const index = state.subscriptions.findIndex(s => s.id === data.id)

    if (index !== -1) {
      state.subscriptions = [
        ...state.subscriptions.slice(0, index),
        data,
        ...state.subscriptions.slice(index + 1)
      ]
    }
  },

  [ADD_CLUB_PORTFOLIOS_MISSING_INFO](state, data) {
    Vue.set(state, 'portfoliosMissingInfo', [...data])
  },

  [ADD_CLUB_PORTFOLIOS_COMPLETE_INFO](state, data) {
    Vue.set(state, 'portfoliosCompleteInfo', [...data])
  },

  [UPDATE_CLUB_SUBSCRIPTION_STATE](state, data) {
    const subIndex = state.subscriptions.findIndex(
      s => s.id === data.subscriptionId
    )

    if (subIndex !== -1) {
      Vue.set(state.subscriptions[subIndex], 'state', data.currentState)
    }
  },

  [ADD_CLUB_PRICING](state, data) {
    Vue.set(state, 'pricing', data)
  },

  [UPDATE_SIGN_URL](state, data) {
    Vue.set(state, 'signUrl', data)
    const docuWindow = window.open(data, 'Docusign')
    docuWindow.focus()
    Vue.set(state, 'docusignWindow', docuWindow)
  },

  [RESET_MODULE](state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  currentSubscription: ({ subscriptions }) => {
    return subscriptions && subscriptions.length ? subscriptions[0] : {}
  },

  isActiveSubscription: (state, getters) => {
    return getters.currentSubscription.state === 'active'
  },

  portfoliosMissingInfo: ({ portfoliosMissingInfo }) => {
    return portfoliosMissingInfo
  },

  isPortfolioInClub: (state, getters) => id => {
    return (
      getters.isActiveSubscription &&
      getters.currentSubscription.portfolios
        .map(p => parseInt(p.id))
        .includes(parseInt(id))
    )
  },

  isPaymentFailed(stateClub, getters) {
    const { reason, state } = getters.currentSubscription
    return (state === 'active' && reason !== 'paid') || (state === 'paused' && reason === 'payment_failure')
  },

  isPaused(state, getters) {
    return (
      getters.currentSubscription.state === 'paused' &&
      getters.currentSubscription.reason === 'payment_failure'
    )
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  getters,
  mutations
})
